import React, { useState, useEffect } from 'react';
import BootstrapDropdown from 'react-bootstrap/Dropdown';
import BootstrapButton from 'react-bootstrap/Button';
import BootstrapOffcanvas from 'react-bootstrap/Offcanvas';
import BootstrapNavbar from 'react-bootstrap/Navbar';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import { ELEVIO_PORTAL_URL, PrivateSubmissionAppRoutes } from '@containers/private-crm-app/constants';
import { useUserContext } from '@containers/private-crm-app/user-context';
import {
  trackViewMyRequests,
  trackViewProfile,
  getElevioPortalHomeUrl,
  getAgencyId,
  getCRMTokenFromUrlOrStorage,
  getPublicMapUrl,
  toAbsoluteNavigationUrl,
} from '@containers/private-crm-app/utils';
import { get } from '@containers/private-crm-app/requests/api';

import { useAuthManagerContext } from '@private-crm-app/auth-manager-context';
import { GoogleTranslateWidget } from '@private-crm-app/components/GoogleTranslateWidget/GoogleTranslateWidget';

import './style.scss';

const Dropdown = BootstrapDropdown as any;
const Button = BootstrapButton as any;
const Offcanvas = BootstrapOffcanvas as any;
const Navbar = BootstrapNavbar as any;

function HomeLink({
  href,
  className,
  children,
}: {
  href: string | null;
  className?: string;
  children: React.ReactNode;
}) {
  if (!href) {
    return <span className={className}>{children}</span>;
  }

  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

export function PrivateSubmissionNavbar() {
  const {
    auth0User,
    logout,
    triggerLoginChoice,
    triggerSignupChoice,
  } = useAuthManagerContext();
  const { user } = useUserContext();

  const elevioPortalURL = localStorage.getItem(ELEVIO_PORTAL_URL) || getElevioPortalHomeUrl();
  const [show, setShow] = useState(false);
  const [agencyLogo, setAgencyLogo] = useState<string | null>(null);
  const [homeLinkUrl, setHomeLinkUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [showAllRequestsLink, setShowAllRequestsLink] = useState(false);
  const homeUrl = toAbsoluteNavigationUrl(homeLinkUrl) || toAbsoluteNavigationUrl(elevioPortalURL);

  // Logo comes from agency info. Home and logo links use the CRM "Back to Home Link URL".
  useEffect(() => {
    let cancelled = false;
    const fetchHeaderLinks = async () => {
      const agencyId = getAgencyId();
      const crmToken = getCRMTokenFromUrlOrStorage();
      const [agencyInfo, crm] = await Promise.all([
        agencyId
          ? get<IAgencyBase>({ url: `agency/${agencyId}/info` }).catch((error) => {
              console.error('Failed to fetch agency info:', error);
              return null;
            })
          : Promise.resolve(null),
        crmToken
          ? get<Pick<IAgencyDetails, 'citizenPortalHomeLinkURL'>>({ url: `crm/${crmToken}` }).catch((error) => {
              console.error('Failed to fetch CRM home link:', error);
              return null;
            })
          : Promise.resolve(null),
      ]);

      if (cancelled) {
        return;
      }
      if (agencyInfo?.logoUrl) {
        setAgencyLogo(agencyInfo.logoUrl);
      }
      if (crm?.citizenPortalHomeLinkURL) {
        setHomeLinkUrl(crm.citizenPortalHomeLinkURL);
      }
      setLogoLoading(false);
    };

    fetchHeaderLinks();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch flags for All Requests link visibility
  useEffect(() => {
    let cancelled = false;
    const loadFlags = async () => {
      const agencyId = getAgencyId();
      const crmToken = getCRMTokenFromUrlOrStorage();
      if (!agencyId || !crmToken) {
        if (!cancelled) setShowAllRequestsLink(false);
        return;
      }
      try {
        const resp = await get<{
          isPublicMapEnabled?: boolean;
        }>({
          url: `agency/${agencyId}/flags`,
          headers: { 'x-agency-id': String(agencyId) },
        });
        if (!cancelled) {
          setShowAllRequestsLink(!!resp?.isPublicMapEnabled);
        }
      } catch {
        if (!cancelled) setShowAllRequestsLink(false);
      }
    };
    loadFlags();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      {/* Desktop Navbar */}
      <Navbar className="private-submission-navbar" aria-label="Primary navigation">
        <div className="private-submission-navbar__container">
          <div className="private-submission-navbar__left">
            {/* Agency Logo - Clickable */}
            {agencyLogo && !logoLoading && (
              <HomeLink href={homeUrl} className="agency-logo">
                <img src={agencyLogo} alt="Agency Logo" className="agency-logo-image" />
              </HomeLink>
            )}
            <HomeLink href={homeUrl}>
              <div className="nav-item">
                <img src="/assets/home.svg" alt="" aria-hidden="true" />
                Home
              </div>
            </HomeLink>
            {auth0User && (
              <Link to={'/private-submission/listing'} onClick={() => trackViewMyRequests(user?.name, user?.email)}>
                <div
                  className={classnames('nav-item', {
                    active: window.location.pathname === PrivateSubmissionAppRoutes.LISTING,
                  })}
                >
                  <img src="/assets/inbox.svg" alt="" aria-hidden="true" />
                  My Requests
                </div>
              </Link>
            )}
            {showAllRequestsLink && (
              <Link
                to={getPublicMapUrl(getCRMTokenFromUrlOrStorage())}
                aria-label="View all requests on map"
              >
                <div
                  className={classnames('nav-item', {
                    active: window.location.pathname === PrivateSubmissionAppRoutes.PUBLIC_MAP,
                  })}
                >
                  <img src="/assets/map.svg" alt="" aria-hidden="true" />
                  All Requests
                </div>
              </Link>
            )}
          </div>
          <div className="private-submission-navbar__right">
            {auth0User ? (
              <Dropdown className="nav-item profile">
                <Dropdown.Toggle variant="link" id="dropdown-custom-components" className="dropdown-toggle">
                  <div
                    style={{
                      backgroundImage: `url(${auth0User?.picture})`,
                    }}
                    className="user-profile-image"
                    aria-label="profile"
                  />
                  {user?.name || auth0User?.name}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    border: 'none',
                    boxShadow: 'none',
                  }}
                >
                  <Dropdown.Item
                    as={Link}
                    to={'/private-submission/profile'}
                    className="dropdown-item dropdown-item-link"
                    onClick={() => trackViewProfile(user?.name, user?.email)}
                  >
                    <span className="dropdown-item-label">Profile</span>
                  </Dropdown.Item>
                  <Dropdown.Item onClick={logout} as="button" className="dropdown-item">
                    Logout
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <>
                <button
                  type="button"
                  className="nav-item nav-item--auth-btn"
                  onClick={triggerLoginChoice}
                >
                  <img src="/assets/login.svg" alt="" aria-hidden="true" />
                  Login
                </button>
                <button
                  type="button"
                  className="nav-item nav-item--auth-btn"
                  onClick={triggerSignupChoice}
                >
                  <img src="/assets/person.svg" alt="" aria-hidden="true" />
                  Sign up
                </button>
              </>
            )}

            {/* Google Translate Widget - Last element */}
            <GoogleTranslateWidget variant="desktop" />
          </div>
        </div>
      </Navbar>
      {/* Mobile Navbar */}
      <Navbar className="private-submission-navbar-mobile" aria-label="Mobile navigation">
        {/* Menu Button */}
        <Button
          variant="link"
          id="dropdown-custom-components"
          className="dropdown-toggle"
          onClick={() => setShow(true)}
          aria-label="Open navigation menu"
        >
          <div className="menu-icon" />
        </Button>
        {/* Offcanvas (Drawer) */}
        <Offcanvas show={show} onHide={() => setShow(false)} placement="end">
          <Offcanvas.Header className="justify-content-end" closeButton />
          <Offcanvas.Body>
            <div>
              {/* Agency Logo for Mobile - Clickable */}
              {agencyLogo && !logoLoading && (
                <div className="display-flex padding">
                  <HomeLink href={homeUrl} className="agency-logo-mobile">
                    <img src={agencyLogo} alt="Agency Logo" className="agency-logo-image-mobile" />
                  </HomeLink>
                </div>
              )}
              <div className="display-flex padding">
                <div className="render-text">
                  <HomeLink href={homeUrl} className="dropdown-item-mobile">
                    <img src="/assets/home.svg" alt="" aria-hidden="true" />
                    <span className="dropdown-item-label-mobile">Home</span>
                  </HomeLink>
                </div>
              </div>
              {auth0User && (
                <div className="display-flex gap-15 padding">
                  <div className="render-text">
                    <Link
                      to={PrivateSubmissionAppRoutes.LISTING}
                      className="dropdown-item-mobile"
                      onClick={() => trackViewMyRequests(user?.name, user?.email)}
                    >
                      <img src="/assets/inbox.svg" alt="" aria-hidden="true" />
                      <span className="dropdown-item-label-mobile">My Requests</span>
                    </Link>
                  </div>
                </div>
              )}
              {showAllRequestsLink && (
                <div className="display-flex gap-15 padding">
                  <div className="render-text">
                    <Link
                      to={getPublicMapUrl(getCRMTokenFromUrlOrStorage())}
                      className="dropdown-item-mobile"
                      onClick={() => setShow(false)}
                      aria-label="View all requests on map"
                    >
                      <img src="/assets/map.svg" alt="" aria-hidden="true" />
                      <span className="dropdown-item-label-mobile">All Requests</span>
                    </Link>
                  </div>
                </div>
              )}

              {auth0User ? (
                <>
                  <div className="display-flex gap-15 padding">
                    <Link
                      to={'/private-submission/profile'}
                      className="dropdown-item-mobile"
                      onClick={() => trackViewProfile(user?.name, user?.email)}
                    >
                      <img src="/assets/profile.svg" alt="" aria-hidden="true" />
                      <span className="dropdown-item-label-mobile">Profile</span>
                    </Link>
                  </div>
                  <div className="display-flex gap-15 padding">
                    <button type="button" onClick={logout} className="dropdown-item-mobile">
                      <img src="/assets/log-out.svg" alt="" aria-hidden="true" />
                      <span className="dropdown-item-label-mobile">Logout</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="display-flex gap-15 padding">
                    <button
                      type="button"
                      className="dropdown-item-mobile"
                      onClick={() => {
                        setShow(false);
                        triggerLoginChoice();
                      }}
                    >
                      <img src="/assets/login.svg" alt="" aria-hidden="true" />
                      <span className="dropdown-item-label-mobile">Login</span>
                    </button>
                  </div>
                  <div className="display-flex gap-15 padding">
                    <button
                      type="button"
                      className="dropdown-item-mobile"
                      onClick={() => {
                        setShow(false);
                        triggerSignupChoice();
                      }}
                    >
                      <img src="/assets/person.svg" alt="" aria-hidden="true" />
                      <span className="dropdown-item-label-mobile">Sign up</span>
                    </button>
                  </div>
                </>
              )}

              {/* Google Translate for Mobile - Last element */}
              <div className="display-flex gap-15 padding">
                <div className="render-text">
                  <div className="google-translate-widget-mobile">
                    <GoogleTranslateWidget variant="mobile" />
                  </div>
                </div>
              </div>
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      </Navbar>
    </>
  );
}
